import * as React from 'react';

// Constants & Enums
import {
  GoogleAuthProvider,
  GOOGLE_OAUTH_BASE_URL,
  GOOGLE_OAUTH_SCOPES,
} from './constants';

// Utils
import { isWebBridgeSupported } from '@shopee-user/platform';
import { createTobSdkEventCallbackListener } from '../../../web-bridge-listener';
import { appendQueryToUrl } from '@shopee/url-util';
import { getWebThirdPartyClientId } from '@shopee-user/business-customization-logic';
import { AuthenticateChannel } from '@tob/shared/thirdparty-auth/core/types';
import {
  generateCodeVerifier,
  generateCodeChallenge,
} from '@tob/shared/security/pkce';
import { createEncryptedState } from '@tob/shared/security/oauth-state';
import { setThirdPartyOAuthContextToStorage } from '@tob/pages/third-party-service/sessionStorage';
import {
  setCurrentUrl,
  replaceCurrentUrl,
} from '@tob/shared/utils/redirection';
import { ThirdpartyBindingEntryPoint } from '@tob/shared/thirdparty-auth/service/constants';
import { AuthMethod } from '@shopee/web-bridge-sdk';
import type { WithUserWebViewProps } from '@shopee-user/with-web-view';

export const useConnectWithGoogle = ({
  redirectUri,
  entryPoint,
  shouldReplaceCurrentUrl = false,
  webViewAuthWith3PServiceByToken,
}: {
  redirectUri: string;
  entryPoint: ThirdpartyBindingEntryPoint;
  shouldReplaceCurrentUrl?: boolean;
  webViewAuthWith3PServiceByToken?: WithUserWebViewProps['webViewAuthWith3PServiceByToken'];
}) => {
  const tobSdkEventListener = React.useRef<ReturnType<
    typeof createTobSdkEventCallbackListener
  > | null>(null);

  React.useEffect(() => {
    return () => {
      if (tobSdkEventListener.current) {
        tobSdkEventListener.current.clear();
      }
    };
  }, []);

  const authenticate = async (): Promise<{
    authProvider: GoogleAuthProvider;
    authCode?: string;
    appClientId?: string;
    isCancel?: boolean;
    error?: number;
    errorMsg?: string;
  }> => {
    if (isWebBridgeSupported()) {
      const result = await authenticateByGoogleApp();
      return {
        ...result,
        authProvider: GoogleAuthProvider.GoogleApp,
      };
    } else {
      await authenticateByGoogleWeb();
      return {
        authProvider: GoogleAuthProvider.GoogleWeb,
      };
    }
  };

  const authenticateByGoogleApp = async (): Promise<{
    authCode?: string;
    appClientId?: string;
    isCancel?: boolean;
    error?: number;
    errorMsg?: string;
  }> => {
    if (!webViewAuthWith3PServiceByToken) {
      return {
        error: 1,
        errorMsg: 'webViewAuthWith3PServiceByToken is not available',
      };
    }

    try {
      const response = await webViewAuthWith3PServiceByToken({
        method: AuthMethod.Google,
      });

      if (response.errorCode !== 0) {
        return {
          error: response.errorCode,
          errorMsg: response.errorMessage,
        };
      }

      if (response.authToken) {
        return {
          authCode: response.authToken,
          appClientId: response.serverClientId,
        };
      }

      return { isCancel: true };
    } catch (err) {
      return {
        error: 1,
        errorMsg: err instanceof Error ? err.message : String(err),
      };
    }
  };

  const authenticateByGoogleWeb = async () => {
    const clientId = getWebThirdPartyClientId({
      channel: AuthenticateChannel.Google,
    });

    const codeVerifier = generateCodeVerifier({});
    const codeChallenge = await generateCodeChallenge({ codeVerifier });
    const { state, keyJwk } = await createEncryptedState({
      type: AuthenticateChannel.Google,
    });

    setThirdPartyOAuthContextToStorage({
      codeVerifier,
      oauthState: state,
      oauthStateKey: keyJwk,
      clientId,
      triggerUrl: window.location.href,
      entryPoint,
    });

    const authUrl = appendQueryToUrl(GOOGLE_OAUTH_BASE_URL, {
      scope: GOOGLE_OAUTH_SCOPES.join(' '),
      response_type: 'code',
      state,
      redirect_uri: redirectUri,
      client_id: clientId,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
    });
    if (shouldReplaceCurrentUrl) {
      replaceCurrentUrl({ url: authUrl, isHardcodedUrl: true });
    } else {
      setCurrentUrl({ url: authUrl, isHardcodedUrl: true });
    }
    return {};
  };

  return {
    authenticate,
  };
};
